import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "components/ui/toast";

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || fallback;

// The file goes straight to storage, so a zip of the whole folder never passes through the backend.
const uploadFile = async (file, onProgress) => {
  const { data } = await api.post(API_ROUTES.LEGISLATION_UPLOAD_URL, {
    name: file.name,
    size: file.size,
  });
  const { key, url, contentType } = data.data;
  await api.put(url, file, {
    headers: { "Content-Type": contentType },
    onUploadProgress: (e) =>
      e.total && onProgress?.(Math.round((e.loaded / e.total) * 100)),
  });
  return key;
};

const invalidateRegister = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: [API_ROUTES.LEGISLATION] });
  // Rules show what they quote, and whether a new version means they need reading again.
  queryClient.invalidateQueries({ queryKey: [API_ROUTES.JURISDICTION_RULES] });
};

export const useGetLegislationQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.LEGISLATION],
    queryFn: () => api.get(API_ROUTES.LEGISLATION),
    // A long Act takes a while to read; keep checking until nothing is being read.
    refetchInterval: (query) =>
      query.state.data?.data?.data?.some((law) => law.status === "processing")
        ? 5000
        : false,
  });
  return { data: data?.data?.data ?? [], isPending, isError, error };
};

export const useGetLawQuery = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.LEGISLATION, { id }],
    queryFn: () => api.get(`${API_ROUTES.LEGISLATION}/${id}`),
    enabled: !!id,
  });
  return { data: data?.data?.data ?? null, isPending, isError, error };
};

export const useAddLegislationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, onProgress }) => {
      const key = await uploadFile(file, onProgress);
      const { data } = await api.post(API_ROUTES.LEGISLATION, {
        key,
        name: file.name,
      });
      return data.data;
    },
    onSuccess: ({ added, skipped }) => {
      showToast(
        added.length ? "success" : "error",
        added.length
          ? `Added ${added.length} ${added.length === 1 ? "law" : "laws"}. Register ${
              added.length === 1 ? "it" : "them"
            } once read.${skipped.length ? ` ${skipped.length} skipped.` : ""}`
          : `Nothing added. ${skipped.map((file) => `${file.name}: ${file.reason}`).join("; ")}`,
      );
      invalidateRegister(queryClient);
    },
    onError: (err) =>
      showToast("error", errorMessage(err, "Failed to add the law")),
  });
};

export const useUpdateLawMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...details }) =>
      api.put(`${API_ROUTES.LEGISLATION}/${id}`, details),
    onSuccess: () => {
      showToast("success", "Register details saved");
      invalidateRegister(queryClient);
    },
    onError: (err) =>
      showToast("error", errorMessage(err, "Failed to save the details")),
  });
};

export const useReadLawAgainMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.put(`${API_ROUTES.LEGISLATION}/${id}/read`),
    onSuccess: () => {
      showToast("success", "Reading it again");
      invalidateRegister(queryClient);
    },
    onError: (err) =>
      showToast("error", errorMessage(err, "Failed to read it again")),
  });
};

export const useDeleteLawMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`${API_ROUTES.LEGISLATION}/${id}`),
    onSuccess: () => {
      showToast("success", "Law deleted");
      invalidateRegister(queryClient);
    },
    onError: (err) =>
      showToast("error", errorMessage(err, "Failed to delete the law")),
  });
};

export const useSearchLegislationMutation = () =>
  useMutation({
    mutationFn: async ({ question, jurisdiction }) => {
      const { data } = await api.post(API_ROUTES.LEGISLATION_SEARCH, {
        question,
        jurisdiction: jurisdiction || undefined,
      });
      return data.data;
    },
    onError: (err) => showToast("error", errorMessage(err, "Search failed")),
  });
